import React from 'react';
import { Container, Spinner } from 'reactstrap';
import Layout from '../../templates/Layout';
import { useMutation, useQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { CREATE_EVENT, GET_EVENTS_BY_ASSET } from '../../../graph-queries/tar/event';
import { TEvent, TEventJobsDelta } from '../../../lib/types';
import AspectsTable from '../../organisms/AspectsTable/AspectsTable';
import { useSelectedAsset } from '../../../lib/util';
import { useHasTarAdminAccess, useHasUserAccess } from '../../../lib/util/auth';
import { formatDate, formatISO } from 'date-fns';
import { CellContext } from '@tanstack/react-table';
import { GET_CONFIG } from '../../../graph-queries/common/config';
import EventForm from '../../organisms/EventForm/EventForm';
import _ from 'lodash';
import { useJobDelta } from './Events.util';
import { Alert16, Check16 } from '@bphxd/ds-core-react/lib/icons';
import DeltaForm from '../../organisms/DeltaForm/DeltaForm';
import { FormModal } from '../../molecules/FormModal/FormModal';
import ReadinessCheck from '../../organisms/ReadinessCheck/ReadinessCheck';
import { Event } from '../../../graph-queries/__generated__/graphql';
import { TARS_AND_OUTAGES_PATH } from '../../../constants';

const dateCell = (props: CellContext<TEvent, unknown>) => {
  return (
    props.getValue() && (
      <span title={formatISO(props.getValue() as number)}>
        {formatDate(props.getValue() as number, 'dd/MM/yyyy')}
      </span>
    )
  );
};

const EventsPage: React.FC = () => {
  const navigate = useNavigate();
  const hasUserAccess = useHasUserAccess();
  const hasTarAdminAccess = useHasTarAdminAccess();
  const asset = useSelectedAsset();
  const { data: config } = useQuery(GET_CONFIG);
  const { data, loading } = useQuery(GET_EVENTS_BY_ASSET, {
    variables: { assetId: asset?.assetId || -1 },
    skip: !asset?.assetId,
  });

  const isEventCreationEnabled = !!config?.config.featureFlags.find(
    (ff) => ff.flag === 'FEATURE_FLAG_ENABLE_EVENT_CREATION'
  )?.isEnabled;

  const isJobDeltaEnabled = !!config?.config.featureFlags.find(
    (ff) => ff.flag === 'FEATURE_FLAG_EVENT_JOB_DELTA'
  )?.isEnabled;

  const isReadinessCheckEnabled = !!config?.config.featureFlags.find(
    (ff) => ff.flag === 'FEATURE_FLAG_READINESS_CHECK'
  )?.isEnabled;

  const events = data?.eventsByAssetId as unknown as TEvent[];
  const { jobsDeltaWithEvent: eventsWithDelta, loading: jobDeltaLoading } = useJobDelta(events, {
    skip: !isJobDeltaEnabled,
  });

  const [createEvent] = useMutation(CREATE_EVENT, { refetchQueries: [GET_EVENTS_BY_ASSET] });

  const onAddSubmit = (event: TEvent) => {
    const input = _.omit(event, ['eventId', 'updatedAt', 'updatedBy']);
    return createEvent({ variables: { input } }).then((res) => {
      navigate(
        `/${TARS_AND_OUTAGES_PATH}/events/${res.data?.createEvent.eventId}?asset=${asset?.assetName}`
      );
    });
  };
  const renderDeltaForm = (onClose: () => void, delta: TEventJobsDelta[]) => {
    return <DeltaForm defaultValues={delta} onClose={onClose} />;
  };

  const isLoading = loading || jobDeltaLoading;
  return (
    <Layout>
      <Container>
        {isLoading ? (
          <div className="d-flex justify-content-center align-items-center vh-100">
            <Spinner size="lg" className="ms-3" label="Loading Events" />
          </div>
        ) : (
          <AspectsTable<TEvent>
            aspectName="event"
            title={`${asset?.displayName} - All events`}
            data={(eventsWithDelta as unknown as TEvent[]) || []}
            hideSelectionColumn
            columns={[
              {
                header: 'Description',
                accessorKey: 'description',
                enableSorting: false,
                cell: (props) => {
                  const row = props.row.original;
                  return (
                    <a
                      href={`/${TARS_AND_OUTAGES_PATH}/events/${row.eventId}?asset=${asset?.assetName}`}
                    >
                      {row.eventDescription}
                    </a>
                  );
                },
              },
              {
                header: 'Start Date',
                accessorKey: 'eventStartDate',
                enableSorting: false,
                cell: dateCell,
              },
              {
                header: 'End Date',
                accessorKey: 'eventEndDate',
                enableSorting: false,
                cell: dateCell,
              },
              {
                header: 'Scope Freeze Date',
                accessorKey: 'eventScopeFreezeDate',
                enableSorting: false,
                cell: dateCell,
              },
              {
                header: 'Revision Code',
                accessorKey: 'revisionCode',
                enableSorting: false,
              },
              ...(isJobDeltaEnabled
                ? [
                    {
                      header: 'SAP Check',
                      accessorKey: 'delta',
                      enableSorting: false,
                      cell: (props: CellContext<TEvent, unknown>) => {
                        const row = props.row.original;
                        return row.delta?.length === 0 ? (
                          <Check16 />
                        ) : (
                          <a style={{ cursor: 'pointer' }}>
                            <FormModal
                              title="Delta With SAP"
                              renderForm={(onClose) =>
                                renderDeltaForm(onClose, row.delta as unknown as TEventJobsDelta[])
                              }
                              renderButton={(onClick) => (
                                <Alert16 onClick={onClick} title={'Click to see the delta'} />
                              )}
                              size="lg"
                            />
                          </a>
                        );
                      },
                    },
                  ]
                : []),
              ...(isReadinessCheckEnabled
                ? [
                    {
                      header: 'Readiness Check',
                      accessorKey: 'readinessCheck',
                      enableSorting: false,
                      cell: (props: CellContext<TEvent, unknown>) => (
                        <ReadinessCheck event={props.row.original as Event} />
                      ),
                    },
                  ]
                : []),
            ]}
            renderAddForm={
              isEventCreationEnabled
                ? (onClose) => (
                    <EventForm
                      onSubmit={onAddSubmit}
                      onClose={onClose}
                      type={'add'}
                      isLoading={isLoading}
                    />
                  )
                : undefined
            }
            creationDisabled={!hasUserAccess}
            getRowMutationDisabled={(_row) => !hasUserAccess}
            getRowDeletionDisabled={(_row) => !hasTarAdminAccess}
          />
        )}
      </Container>
    </Layout>
  );
};

export default EventsPage;
